/**
 * Project warning utilities
 * Provides functions to detect and categorize project issues
 */

import { Project } from "../models/Project";
import { Status } from "../types";
import { formatDateFrench } from "./dateFormatting";

export interface ProjectWarning {
  type: "error" | "warning" | "info";
  message: string;
  icon: string;
}

/**
 * Check if a project has any warnings or issues
 * @param project - Project to check
 * @returns Array of warnings (empty if no issues)
 */
export function getProjectWarnings(project: Project): ProjectWarning[] {
  const warnings: ProjectWarning[] = [];
  const today = new Date();
  today.setHours(0, 0, 0, 0); // Reset to start of day for comparison

  // Warning 1: Not done with end date in the past
  if (
    project.status !== Status.DONE &&
    project.endDate &&
    project.endDate < today
  ) {
    warnings.push({
      type: "error",
      message: `Project is not marked as Done but end date (${formatDate(
        project.endDate
      )}) is in the past`,
      icon: "⚠️",
    });
  }

  // Warning 2: Done with end date in the future
  if (
    project.status === Status.DONE &&
    project.endDate &&
    project.endDate > today
  ) {
    warnings.push({
      type: "warning",
      message: `Project is marked as Done but end date (${formatDate(
        project.endDate
      )}) is in the future`,
      icon: "⚠️",
    });
  }

  // Warning 3: In Progress or Planned with no start date
  if (
    (project.status === Status.IN_PROGRESS ||
      project.status === Status.PLANNED) &&
    !project.startDate
  ) {
    warnings.push({
      type: "error",
      message: "Project is In Progress or Planned but has no start date",
      icon: "⚠️",
    });
  }

  // Warning 4: In Progress or Planned with no end date
  if (
    (project.status === Status.IN_PROGRESS ||
      project.status === Status.PLANNED) &&
    !project.endDate
  ) {
    warnings.push({
      type: "error",
      message: "Project is In Progress or Planned but has no end date",
      icon: "⚠️",
    });
  }

  // Warning 5: In Progress or Planned with zero total effort
  if (
    (project.status === Status.IN_PROGRESS ||
      project.status === Status.PLANNED) &&
    project.effort === 0
  ) {
    warnings.push({
      type: "error",
      message: "Project is In Progress or Planned but has zero total effort",
      icon: "⚠️",
    });
  }

  return warnings;
}

/**
 * Check if a project has any warnings
 * @param project - Project to check
 * @returns True if project has warnings
 */
export function hasWarnings(project: Project): boolean {
  return getProjectWarnings(project).length > 0;
}

/**
 * Get the highest severity warning for a project
 * @param project - Project to check
 * @returns The most severe warning, or null if no warnings
 */
export function getPrimaryWarning(project: Project): ProjectWarning | null {
  const warnings = getProjectWarnings(project);
  if (warnings.length === 0) return null;

  // Priority: error > warning > info
  const error = warnings.find((w) => w.type === "error");
  if (error) return error;

  const warning = warnings.find((w) => w.type === "warning");
  if (warning) return warning;

  return warnings[0];
}

/**
 * Format a date for display in warning messages
 */
function formatDate(date: Date): string {
  return formatDateFrench(date);
}

/**
 * Get a tooltip text with all warnings for a project
 * @param project - Project to check
 * @returns Formatted tooltip text, or empty string if no warnings
 */
export function getWarningTooltip(project: Project): string {
  const warnings = getProjectWarnings(project);
  if (warnings.length === 0) return "";

  return warnings.map((w) => `${w.icon} ${w.message}`).join("\n");
}
